import React, { useState, useRef, useEffect } from 'react';

const ClientSelectDropdown = ({
  value,
  onChange,
  options,
  loading,
  disabled,
  isDarkMode,
  required,
  placeholder = 'Sélectionner un client',
  defaultValue
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef();

  useEffect(() => {
    if (!value && defaultValue) {
      onChange(defaultValue);
    }
  }, [defaultValue, value]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const filteredOptions = options.filter(
    (client) =>
      client.raisonSocial?.toLowerCase().includes(search.toLowerCase())
  );

  const selected = options.find((c) => String(c.id) === String(value));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`w-full px-4 py-3 border rounded-lg text-left flex justify-between items-center ${
          isDarkMode
            ? 'bg-gray-800 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        }`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        tabIndex={0}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {selected ? (
            <>
              {selected.logo ? (
                <div className="w-10 h-10 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden bg-white p-1">
                  <img 
                    src={`data:image/jpeg;base64,${selected.logo}`}
                    alt={selected.raisonSocial}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.parentElement.innerHTML = '<i class="fas fa-building text-gray-400 flex items-center justify-center h-full"></i>';
                    }}
                  />
                </div>
              ) : (
                <div className={`w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <i className="fas fa-building text-gray-400"></i>
                </div>
              )}
              <span className="truncate">{selected.raisonSocial}</span>
            </>
          ) : (
            <span className="truncate">{loading ? 'Chargement...' : placeholder}</span>
          )}
        </div>
        <i className={`fas fa-chevron-down ml-2 flex-shrink-0 ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div
          className={`absolute z-50 left-0 right-0 mt-2 rounded-lg shadow-lg border ${
            isDarkMode
              ? 'bg-gray-800 border-gray-600'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className="p-2 border-b border-gray-200 sticky top-0 z-10 bg-inherit">
            <input
              type="text"
              autoFocus
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full px-3 py-2 border rounded ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
          <div className="max-h-60 overflow-auto">
            {filteredOptions.length === 0 && (
              <div className={`px-4 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Aucun client trouvé
              </div>
            )}
            {filteredOptions.map((client) => (
              <button
                key={client.id}
                type="button"
                className={`w-full text-left px-4 py-2 hover:bg-primary-50 ${
                  isDarkMode
                    ? String(value) === String(client.id)
                      ? 'bg-primary-700 text-white'
                      : 'text-white hover:bg-gray-700'
                    : String(value) === String(client.id)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-900'
                }`}
                onClick={() => {
                  onChange(client.id);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <div className="flex items-center gap-3">
                  {client.logo ? (
                    <div className="w-10 h-10 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden bg-white p-1">
                      <img 
                        src={`data:image/jpeg;base64,${client.logo}`}
                        alt={client.raisonSocial}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.parentElement.innerHTML = '<i class="fas fa-building text-gray-400 flex items-center justify-center h-full"></i>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className={`w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <i className="fas fa-building text-gray-400"></i>
                    </div>
                  )}
                  <span>{client.raisonSocial}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {required && (
        <input
          tabIndex={-1}
          style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
          value={value || ''}
          required
          readOnly
        />
      )}
    </div>
  );
};

export default ClientSelectDropdown;